import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TodueWordmark } from '@/components/Logo';
import ThemeToggle from '@/components/ThemeToggle';
import SectionIcon from '@/components/SectionIcon';
import Avatar from '@/components/Avatar';
import SidebarSearch from '@/components/SidebarSearch';
import SidebarAccountSwitcher from '@/components/SidebarAccountSwitcher';
import { I } from '@/lib/icons';
import { SECTIONS } from '@/lib/sections';
import { useWorkspace, useClients, useInvoices, useExpenses, useDocuments } from '@/hooks/useData';
import { FEATURES } from '@/lib/featureFlags';

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { workspace } = useWorkspace();
  const { invoices } = useInvoices(workspace?.id);
  const { expenses } = useExpenses(workspace?.id);
  const { clients } = useClients(workspace?.id);
  // Pass null when the documents flag is off — hook short-circuits, no Supabase query.
  const { documents } = useDocuments(FEATURES.documents ? workspace?.id : null);

  const sectionItems = SECTIONS.map(s => {
    let count = null;
    if (s.id === 'faktury') count = invoices.length;
    else if (s.id === 'vydavky') count = expenses.length;
    else if (s.id === 'dokumenty') count = documents.length;
    else if (s.id === 'klienti') count = clients.length;
    return { ...s, count };
  });

  const pinned = clients.filter(c => c.is_pinned);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <aside className="cd-sidebar" style={{
      width: 260, background: 'var(--bg-alt)',
      borderRight: '1px solid var(--border)',
      display: 'flex', flexDirection: 'column', flexShrink: 0,
    }}>
      {/* Brand header */}
      <div style={{ padding: '14px 12px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <TodueWordmark height={36} />
        <ThemeToggle size={16} />
      </div>

      {/* Search */}
      <div style={{ padding: '20px 8px 10px' }}>
        <SidebarSearch />
      </div>

      {/* Section list */}
      <div className="cd-scroll" style={{ padding: '4px 8px 8px', flex: 1, overflowY: 'auto' }}>
        <div style={{
          fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
          padding: '4px 10px 6px', letterSpacing: '0.02em',
        }}>Moje sekcie</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {sectionItems.map(s => {
            const active = isActive(s.path);
            return (
              <button key={s.id} data-testid={`sidebar-${s.id}`} onClick={() => navigate(s.path)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px',
                  border: 'none', textAlign: 'left',
                  background: active ? `var(--sec-${s.color}-soft)` : 'transparent',
                  color: active ? `var(--sec-${s.color})` : 'var(--text-primary)',
                  borderRadius: 'var(--r-sm)', cursor: 'pointer',
                  fontWeight: active ? 600 : 500, fontFamily: 'inherit',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                <SectionIcon color={s.color} size={22}>
                  {React.createElement(I[s.icon], { width: 13, height: 13 })}
                </SectionIcon>
                <span style={{ fontSize: 13, flex: 1 }}>{s.label}</span>
                {s.count != null && (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                    {s.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Pinned clients */}
        {pinned.length > 0 && (
          <>
            <div style={{
              fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
              padding: '16px 10px 6px', letterSpacing: '0.02em',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <I.star /> Hviezdny klienti
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {pinned.map(c => (
                <button key={c.id} onClick={() => navigate(`/klienti/${c.id}`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '5px 10px', border: 'none', background: 'transparent',
                    borderRadius: 'var(--r-sm)', cursor: 'pointer', textAlign: 'left',
                    fontFamily: 'inherit',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <Avatar name={c.name} size={20} />
                  <span style={{
                    fontSize: 12.5, color: 'var(--text-primary)', flex: 1,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{c.name}</span>
                  <span style={{ color: 'var(--sec-yellow)', display: 'inline-flex', flexShrink: 0 }}>
                    <I.star />
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Wallet pocket account switcher — replaces AccountPanel.
          Žiadny border-top — karty visia priamo, separator je vizuálne v shadowe karty. */}
      <SidebarAccountSwitcher />
    </aside>
  );
}
